import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components'
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { AlertPosition, AlertType, DeviceType, MemberType, ModalType } from '../../shared/enum/EnumRepository';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useCheckBeforeMove } from '../../shared/hooks/useCheckBeforeMove';
import { scrollToChangedElement } from '../../shared/hooks/useUnsavedChanges';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { useEditModeStore } from '../../shared/store/authStore';
import { openNewTab } from '../../shared/util/openNewTab';
import { pageTypeToGuideTab } from '../../page/guide/guideContent';
import { useSettingStore } from '../../shared/store/settingStore';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { useIsFromArchitrave } from '../../shared/hooks/useIsFromArchitrave';
import belowIcon from '../../asset/icon/below.png';

/** 상단 라벨 ↔ 메뉴, 메뉴 그룹 간 */
const SPACE_SECTION = 26;
const SPACE_SECTION_MOBILE = 16;
/** 라벨/메뉴 ↔ 토글 (닫힘·열림 공통, 타이트) */
const SPACE_TOGGLE = 4;
/** Expandable 열림/닫힘 transition */
const EXPAND_MS = 500;
/** 영역 leave 후 자동 닫힘 */
const CLOSE_DELAY_MS = 500;

const RightTopIsland: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { getCurrentPage, checkPageHasChanges } = useCheckBeforeMove();
  const { user, logout, isOwner } = useAuth();
  const { setStandardModal } = useModalStore();
  const { enqueueAlert } = useStandardAlertStore();
  const { pathname, search } = useLocation();
  const { deviceType } = useDeviceStore();
  const setting = useSettingStore((s) => s.setting);
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const isFromArchitrave = useIsFromArchitrave();

  const isLanding = pathname === '/';
  const isExhibitions = pathname === '/exhibitions';
  const isGuide = pathname === '/guide';
  const isMobile = deviceType === DeviceType.MOBILE;
  const usesMobileArchiveNav =
    deviceType === DeviceType.MOBILE || deviceType === DeviceType.TABLET;
  const spaceSection = isMobile ? SPACE_SECTION_MOBILE : SPACE_SECTION;
  const isOnMyArchive = !!user && (
    pathname === `/@${user.handle}` ||
    pathname.startsWith(`/@${user.handle}/`)
  );
  const showArchiveLabel = !!user && !isOnMyArchive;
  /** 아카이브(pageOwner+setting)에서만. Fold/Fixed 공통 */
  const isOnArchive = !isFromArchitrave && !!pageOwner && !!setting;
  const showArchitrave = isOnArchive;
  const showIndexLink = showArchitrave || isExhibitions;
  /** 소유자 + 아카이브. 모바일·태블릿은 햄버거에 Settings가 있음 */
  const showSettings = isOwner && isOnArchive && !usesMobileArchiveNav;
  const isExpanded = isLanding || isExhibitions || isOpen;

  const clearCloseTimer = () => {
    if (closeTimerRef.current == null) return;
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  };

  const openMenu = () => {
    if (isLanding || isExhibitions) return;
    clearCloseTimer();
    setIsOpen(true);
  };

  const scheduleClose = () => {
    if (isLanding || isExhibitions) return;
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      closeTimerRef.current = null;
    }, CLOSE_DELAY_MS);
  };

  useEffect(() => () => clearCloseTimer(), []);

  /** guide는 섬을 숨기만 하고 언마운트하지 않으므로, 진입 시 열림 상태 리셋 */
  useEffect(() => {
    if (!isGuide) return;
    clearCloseTimer();
    setIsOpen(false);
  }, [isGuide]);

  /** 모바일: 영역 밖 탭 시 즉시 접기 (mouseleave 대체) */
  useEffect(() => {
    if (!isMobile || isLanding || isExhibitions || !isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      clearCloseTimer();
      setIsOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isMobile, isLanding, isExhibitions, isOpen]);

  const loginHandler = () => {
    setStandardModal({
      modalType: ModalType.LOGIN,
      title: null,
      value: null,
      handleChange: () => { }
    });
  };

  const openLandingSearch = () => {
    setStandardModal({
      modalType: ModalType.LANDING_MEMBER_SEARCH,
      title: null,
      value: null,
      handleChange: () => { },
    });
  };

  /** 로그인한 사용자의 아카이브 홈으로 이동한다. */
  const moveToMyPage = () => {
    if (user == null) return null;

    // 애초에 주인이 아니면 변경사항 확인할 필요가 없다.
    if (!isOwner) {
      navigate(`/@${user.handle}/`);
      return;
    }
    const { page } = getCurrentPage();
    // 변경사항 있을 것이 없는 페이지는 그냥 이동
    if (page === 'policy' || page === 'guide' || page === 'ADMIN' || page === 'poll') {
      navigate(`/@${user.handle}/`);
      return;
    }
    if (page === 'billboard') return;

    navigate(`/@${user.handle}/`);
  }

  /**
   * 로그인 ADMIN 유저에게만 보이고
   * ADMIN 페이지에 이동하더라도 백엔드에서 확인하니까 
   * 문제없음
   */
  const moveToAdmin = () => {
    if (user == null) return null;
    if (user.role !== "ADMIN") return null;
    navigate("/admin");
    return;
  }

  const openGuide = () => {
    if (pathname === '/guide') return;
    const guideTab = pageTypeToGuideTab(getCurrentPage().page);
    const guideState = {
      guideTab,
      returnTo: pathname + search,
      showGroupGuides: setting?.memberType === MemberType.GROUP,
    };
    navigate('/guide', { state: guideState });
  };

  const logoutHandler = () => {
    const isChanged = checkPageHasChanges();
    if (isChanged) {
      setTimeout(() => scrollToChangedElement(), 100); // 약간의 지연으로 DOM 업데이트 대기
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('CONFIRM_LOGOUT_UNSAVED'),
        callBack: async () => {
          useEditModeStore.getState().setIsEditMode(false);
          await logout();
          window.location.reload();
        }
      })
    } else {
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('CONFIRM_LOGOUT'),
        callBack: logout
      })
    };
  }

  const handleNotice = () => {
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_GO_INSTAGRAM'),
      callBack: () => {
        openNewTab('https://instagram.com/_u/architrave.work');
      }
    });
  }

  const handleSupport = () => {
    if (!user) return;
    setStandardModal({
      modalType: ModalType.SUPPORT,
      title: "Support",
      value: "",
      handleChange: () => { }
    });
  }

  const moveToArchitrave = () => {
    navigate('/');
  };

  const moveToExhibitions = () => {
    if (pathname === '/exhibitions') return;
    navigate('/exhibitions');
  };

  const moveToSettings = () => {
    if (!pageOwner) return;
    if (getCurrentPage().page === 'settings') return;
    navigate(`/@${pageOwner.handle}/settings`);
  };

  /** 랜딩 검색: 로그인 시 Archive 옆, 비로그인 시 Login 옆 (동시에 둘 다 노출되지 않음) */
  const renderLandingSearchBtn = () =>
    isLanding ? (
      <TextBtn onClick={openLandingSearch}>
        {getBtnName('SEARCH')}
      </TextBtn>
    ) : null;

  if (isGuide) return null;

  return (
    <GnbSection
      ref={rootRef}
      $isMobile={isMobile}
      onMouseEnter={clearCloseTimer}
      onMouseLeave={scheduleClose}
    >
      <BtnContainer>
        {showArchiveLabel && (
          <TextBtn onClick={moveToMyPage}>
            {getBtnName('MY_ARCHIVE_LABEL')}
          </TextBtn>
        )}
        {showArchiveLabel && renderLandingSearchBtn()}

        <Expandable
          $isExpanded={isExpanded}
          $gapFromTopLabel={showArchiveLabel}
          $spaceSection={spaceSection}
          aria-hidden={!isExpanded}
        >
          {!user && (
            <BtnGroup>
              <TextBtn onClick={loginHandler}>
                {getBtnName('LOGIN')}
              </TextBtn>
              {renderLandingSearchBtn()}
            </BtnGroup>
          )}

          <BtnGroup>
            {isLanding && (
              <TextBtn onClick={handleNotice}>
                {getBtnName('NOTICE')}
              </TextBtn>
            )}
            <TextBtn onClick={openGuide}>
              {getBtnName('GUIDE')}
            </TextBtn>
            {user && isLanding && (
              <TextBtn onClick={handleSupport}>
                {getBtnName('SUPPORT')}
              </TextBtn>
            )}
            {isLanding && (
              <TextBtn onClick={moveToExhibitions}>
                {getBtnName('EXHIBITION_LABEL')}
              </TextBtn>
            )}
          </BtnGroup>
          {user && (
            <BtnGroup>
              {showSettings && (
                <TextBtn onClick={moveToSettings}>
                  {getBtnName('SETTINGS')}
                </TextBtn>
              )}
              {user.role === "ADMIN" && (
                <TextBtn onClick={moveToAdmin}>
                  ADMIN
                </TextBtn>
              )}
              <TextBtn onClick={logoutHandler}>
                {getBtnName('LOGOUT')}
              </TextBtn>
            </BtnGroup>
          )}
          {showIndexLink && (
            <BtnGroup>
              <TextBtn onClick={moveToArchitrave}>
                {getBtnName('INDEX')}
              </TextBtn>
            </BtnGroup>
          )}
        </Expandable>

        {!isLanding && !isExhibitions && !isOpen && (
          <ToggleBtn
            type="button"
            aria-label="Expand menu"
            aria-expanded={false}
            onMouseEnter={openMenu}
            onClick={() => {
              clearCloseTimer();
              setIsOpen(true);
            }}
          >
            <ToggleIcon $backgroundImg={belowIcon} />
          </ToggleBtn>
        )}
      </BtnContainer>
    </GnbSection>
  );
}

const GnbSection = styled.section<{ $isMobile: boolean }>`
  position: fixed;
  top: ${({ $isMobile }) => $isMobile ? 'clamp(18px, 5vw, 24px)' : 'clamp(20px, 2vw, 30px)'};
  right: ${({ $isMobile }) => $isMobile ? 'clamp(18px, 5vw, 24px)' : 'clamp(20px, 2vw, 30px)'};
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  z-index: 4;
`;

/** gap 없음 — 중간 Expandable이 닫혀도 flex gap이 쌓이지 않게 */
const BtnContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const TextBtn = styled.button`
  padding: 0;
  background: transparent;
  padding: 0px 0 4px 12px;
  border: none;
  outline: none;
  cursor: pointer;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  transform-origin: right bottom;
  transition: transform 0.3s ease-in-out;

  &:hover {
    transform: scale(0.95);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * 닫힘: max-height/margin 0 → 아카이브 라벨(있을 때)과 토글만
 * 열림 + 아카이브 라벨: margin-top으로 섹션 간격
 * 비로그인 열림: LOGIN 그룹 ↔ 메뉴 그룹은 gap으로 간격
 */
const Expandable = styled.div<{
  $isExpanded: boolean;
  $gapFromTopLabel: boolean;
  $spaceSection: number;
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ $spaceSection }) => $spaceSection}px;
  max-height: ${({ $isExpanded }) => ($isExpanded ? '320px' : '0')};
  margin-top: ${({ $isExpanded, $gapFromTopLabel, $spaceSection }) =>
    $isExpanded && $gapFromTopLabel ? `${$spaceSection}px` : '0'};
  overflow: hidden;
  opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
  visibility: ${({ $isExpanded }) => ($isExpanded ? 'visible' : 'hidden')};
  pointer-events: ${({ $isExpanded }) => ($isExpanded ? 'auto' : 'none')};
  transition:
    max-height ${EXPAND_MS}ms ease-in-out,
    margin-top ${EXPAND_MS}ms ease-in-out,
    opacity ${EXPAND_MS}ms ease-in-out,
    visibility 0s ${({ $isExpanded }) => ($isExpanded ? '0s' : `${EXPAND_MS}ms`)};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const BtnGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const ToggleBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: ${SPACE_TOGGLE}px;
  padding: 0 0 4px 8px;
  border: 0;
  background: transparent;
  cursor: pointer;
`;

const ToggleIcon = styled.span<{ $backgroundImg: string }>`
  display: block;
  width: 8px;
  height: 8px;
  background-image: url(${({ $backgroundImg }) => $backgroundImg});
  background-size: cover;
  background-position: center;
`;

export default RightTopIsland;
